import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { debounce } from 'lodash';
import { Col, Input, Row } from 'reactstrap';
import { faSearch } from '@fortawesome/pro-light-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../common/types';

interface SmartLinksSearchBarProps {
  /** The search of the URL. */
  value: string;
  onChange: (search: string) => void;
}

/** The search of the SmartLinks page (it was on the old full list). It finds a page by name or address. */
const SmartLinksSearchBarComponent: React.FC<SmartLinksSearchBarProps> = ({ value, onChange }) => {
  const intl = useIntl();
  const [text, setText] = useState(value);
  const lastSent = useRef(value);

  // Back, Forward and a deep link change the URL: show that search in the box.
  useEffect(() => {
    if (value !== lastSent.current) {
      lastSent.current = value;
      setText(value);
    }
  }, [value]);

  const sendDebounced = useMemo(
    () =>
      debounce((next: string) => {
        lastSent.current = next;
        onChange(next);
      }, 400),
    [onChange]
  );
  useEffect(() => () => sendDebounced.cancel(), [sendDebounced]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
      sendDebounced(event.target.value);
    },
    [sendDebounced]
  );

  const label = intl.formatMessage({
    id: 'app.sml.smartlinks.search.placeholder',
    defaultMessage: 'Search by name or address',
  });

  return (
    <Row className="mb-3">
      <Col xs={12} md={8} lg={6} className="sml-search-bar d-flex align-items-center">
        <FontAwesomeIcon icon={faSearch} className="position-absolute search-icon" />
        <Input
          type="search"
          className="search-input"
          value={text}
          onChange={handleChange}
          placeholder={label}
          aria-label={label}
        />
      </Col>
    </Row>
  );
};

export const SmartLinksSearchBar = TypedMemo(SmartLinksSearchBarComponent);
